import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bookmark, Heart, Link2, MessageCircle, MoreHorizontal, Send, Share2, Trash2 } from 'lucide-react';
import { postService } from '../../services/postService';
import { userService } from '../../services/userService';
import { useConfirm, useToast } from './Toast';
import type { WorkComment } from './VideoModal';

const COMMENT_THREAD_CACHE_TTL_MS = 60 * 1000;
const hasStoredToken = () => Boolean(localStorage.getItem('token'));

const commentThreadCache = new Map<string, {
    comments: WorkComment[];
    totalCount: number;
    cachedAt: number;
}>();

type CommentThreadSectionProps = {
    threadId?: string | null;
    initialComments?: WorkComment[];
    initialCommentCount?: number;
    commentsEnabled?: boolean;
    commentPlaceholder?: string;
    threadOwnerUserId?: string;
    onCommentsChange?: (comments: WorkComment[], commentCount: number) => void;
    interactionBar?: {
        likeCount: number;
        favoriteCount: number;
        isLiked: boolean;
        isFavorited: boolean;
        canToggleLike: boolean;
        canToggleBookmark: boolean;
        onToggleLike?: () => void | Promise<void>;
        onToggleBookmark?: () => void | Promise<void>;
        onShare?: () => void;
        shareCopied?: boolean;
    };
};

const updateCommentTree = (
    comments: WorkComment[],
    targetId: string,
    updater: (comment: WorkComment) => WorkComment
): WorkComment[] => (
    comments.map((comment) => {
        if (String(comment.id) === targetId) {
            return updater(comment);
        }

        if (comment.replies?.length) {
            return {
                ...comment,
                replies: updateCommentTree(comment.replies, targetId, updater)
            };
        }

        return comment;
    })
);

const prependReplyToTree = (
    comments: WorkComment[],
    parentId: string,
    reply: WorkComment
): WorkComment[] => updateCommentTree(comments, parentId, (comment) => ({
    ...comment,
    replies: [reply, ...(comment.replies || [])]
}));

const countCommentBranch = (comment: WorkComment): number => (
    1 + (comment.replies || []).reduce((total, reply) => total + countCommentBranch(reply), 0)
);

const findCommentInTree = (
    comments: WorkComment[],
    targetId: string
): WorkComment | null => {
    for (const comment of comments) {
        if (String(comment.id) === targetId) {
            return comment;
        }

        if (comment.replies?.length) {
            const nested = findCommentInTree(comment.replies, targetId);
            if (nested) {
                return nested;
            }
        }
    }

    return null;
};

const removeCommentFromTree = (
    comments: WorkComment[],
    targetId: string
): { nextComments: WorkComment[]; removedCount: number } => {
    let removedCount = 0;

    const nextComments = comments.flatMap((comment) => {
        if (String(comment.id) === targetId) {
            removedCount += countCommentBranch(comment);
            return [];
        }

        if (comment.replies?.length) {
            const nested = removeCommentFromTree(comment.replies, targetId);
            if (nested.removedCount > 0) {
                removedCount += nested.removedCount;
                return [{ ...comment, replies: nested.nextComments }];
            }
        }

        return [comment];
    });

    return { nextComments, removedCount };
};

const writeCommentThreadCache = (threadId: string, comments: WorkComment[], totalCount: number) => {
    commentThreadCache.set(threadId, {
        comments,
        totalCount,
        cachedAt: Date.now()
    });
};

const CommentThreadSection: React.FC<CommentThreadSectionProps> = ({
    threadId,
    initialComments = [],
    initialCommentCount = initialComments.length,
    commentsEnabled = true,
    commentPlaceholder = '发表评论...',
    threadOwnerUserId,
    onCommentsChange,
    interactionBar
}) => {
    const navigate = useNavigate();
    const [comments, setComments] = useState<WorkComment[]>(initialComments);
    const [commentCount, setCommentCount] = useState(initialCommentCount);
    const [commentText, setCommentText] = useState('');
    const [replyTarget, setReplyTarget] = useState<{ id: string; username: string } | null>(null);
    const [isLoadingComments, setIsLoadingComments] = useState(false);
    const [isSubmittingComment, setIsSubmittingComment] = useState(false);
    const [likingCommentIds, setLikingCommentIds] = useState<string[]>([]);
    const [deletingCommentIds, setDeletingCommentIds] = useState<string[]>([]);
    const [currentUserId, setCurrentUserId] = useState<string | null>(null);
    const [hasSession, setHasSession] = useState<boolean>(() => hasStoredToken());
    const [openCommentMenuId, setOpenCommentMenuId] = useState<string | null>(null);

    const { showToast } = useToast();
    const { confirm } = useConfirm();

    useEffect(() => {
        setComments(initialComments);
        setCommentCount(initialCommentCount);
        setCommentText('');
        setReplyTarget(null);

        if (threadId && initialComments.length > 0) {
            writeCommentThreadCache(threadId, initialComments, initialCommentCount);
        }
    }, [threadId, initialComments, initialCommentCount]);

    useEffect(() => {
        onCommentsChange?.(comments, commentCount);
    }, [commentCount, comments, onCommentsChange]);

    useEffect(() => {
        if (!threadId) {
            return;
        }

        if (comments.length === 0 && commentCount > 0) {
            return;
        }

        // Don't write a placeholder empty cache here. If we did, the
        // subsequent loadComments() call would see hasUsableCache=true
        // (totalCount===0) and short-circuit without ever fetching from
        // the API — the modal would show "暂无评论" forever. Only persist
        // the cache when we genuinely have comments to remember;
        // loadComments writes the cache itself after a real API hit so
        // truly-empty threads still get an authoritative cache entry.
        if (comments.length === 0) {
            return;
        }

        writeCommentThreadCache(threadId, comments, commentCount);
    }, [threadId, comments, commentCount]);

    const canOperateComments = commentsEnabled && !!threadId;

    const resolvedPlaceholder = useMemo(() => {
        if (!canOperateComments) {
            return '当前内容暂不支持评论';
        }

        if (replyTarget) {
            return `回复 @${replyTarget.username}...`;
        }

        return commentPlaceholder;
    }, [canOperateComments, commentPlaceholder, replyTarget]);

    const loadComments = useCallback(async (showError = true) => {
        if (!threadId) {
            setComments([]);
            setCommentCount(0);
            return;
        }

        const cached = commentThreadCache.get(threadId);
        const hasUsableCache = !!cached && (cached.comments.length > 0 || cached.totalCount === 0);
        const shouldUseCache = !!cached && hasUsableCache && (Date.now() - cached.cachedAt) < COMMENT_THREAD_CACHE_TTL_MS;

        if (shouldUseCache && cached) {
            setComments(cached.comments);
            setCommentCount(cached.totalCount);
            setIsLoadingComments(false);
            return;
        }

        if (cached && hasUsableCache) {
            setComments(cached.comments);
            setCommentCount(cached.totalCount);
        }

        setIsLoadingComments(!cached || !hasUsableCache || cached.comments.length === 0);
        const response = await postService.getPostComments(threadId, 50);
        setIsLoadingComments(false);

        if (!response.success || !response.data) {
            if (showError) {
                showToast(response.error || '评论加载失败', 'error');
            }
            return;
        }

        setComments(response.data.comments);
        setCommentCount(response.data.totalCount);
        writeCommentThreadCache(threadId, response.data.comments, response.data.totalCount);
    }, [showToast, threadId]);

    useEffect(() => {
        if (!canOperateComments) {
            return;
        }

        void loadComments(false);
    }, [canOperateComments, loadComments]);

    useEffect(() => {
        const syncSession = () => {
            const nextHasSession = hasStoredToken();
            setHasSession(nextHasSession);
            if (!nextHasSession) {
                setCurrentUserId(null);
            }
        };

        window.addEventListener('auth-change', syncSession);
        window.addEventListener('storage', syncSession);

        return () => {
            window.removeEventListener('auth-change', syncSession);
            window.removeEventListener('storage', syncSession);
        };
    }, []);

    useEffect(() => {
        if (!canOperateComments || currentUserId) {
            return;
        }
        if (!hasSession) {
            return;
        }

        let isMounted = true;
        void userService.getUserProfile({ suppressAuthPrompt: true }).then((response) => {
            if (!isMounted || !response.success || !response.data?.userProfile) {
                return;
            }

            setCurrentUserId(response.data.userProfile.userId);
        });

        return () => {
            isMounted = false;
        };
    }, [canOperateComments, currentUserId, hasSession]);

    useEffect(() => {
        if (!openCommentMenuId) {
            return;
        }

        const handlePointerDown = (event: MouseEvent) => {
            const target = event.target as HTMLElement | null;
            if (target?.closest('[data-comment-actions-root="true"]')) {
                return;
            }
            setOpenCommentMenuId(null);
        };

        document.addEventListener('mousedown', handlePointerDown);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
        };
    }, [openCommentMenuId]);

    const handleSubmitComment = async () => {
        if (!threadId || !commentText.trim() || !canOperateComments || isSubmittingComment) {
            return;
        }

        setIsSubmittingComment(true);
        const response = await postService.createComment(
            threadId,
            commentText.trim(),
            replyTarget?.id
        );
        setIsSubmittingComment(false);

        if (!response.success) {
            showToast(response.error || '评论发布失败', 'error');
            return;
        }

        const createdComment = response.data as WorkComment | undefined;
        if (createdComment) {
            setComments((prev) => {
                let nextComments: WorkComment[];
                if (replyTarget) {
                    nextComments = prependReplyToTree(prev, replyTarget.id, createdComment);
                } else {
                    nextComments = [createdComment, ...prev];
                }

                if (threadId) {
                    writeCommentThreadCache(threadId, nextComments, commentCount + 1);
                }

                return nextComments;
            });
            setCommentCount((prev) => prev + 1);
        }

        if (threadId.startsWith('post_')) {
            window.dispatchEvent(new Event('interactions_updated'));
        }

        showToast(replyTarget ? '回复发布成功' : '评论发布成功', 'success');
        setCommentText('');
        setReplyTarget(null);
    };

    const handleToggleCommentLike = async (commentId: string | number) => {
        if (!threadId) {
            return;
        }

        const targetId = String(commentId);
        if (likingCommentIds.includes(targetId)) {
            return;
        }

        setLikingCommentIds((prev) => [...prev, targetId]);
        const response = await postService.toggleCommentLike(threadId, targetId);
        setLikingCommentIds((prev) => prev.filter((id) => id !== targetId));

        if (!response.success) {
            showToast(response.error || '评论点赞失败', 'error');
            return;
        }

        const nextLiked = !!response.data?.liked;
        const nextLikeCount = response.data?.likeCount;

        setComments((prev) => {
            const nextComments = updateCommentTree(prev, targetId, (comment) => ({
                ...comment,
                liked: nextLiked,
                likes: typeof nextLikeCount === 'number'
                    ? nextLikeCount
                    : Math.max(0, comment.likes + (nextLiked ? 1 : -1))
            }));

            if (threadId) {
                writeCommentThreadCache(threadId, nextComments, commentCount);
            }

            return nextComments;
        });
    };

    const canDeleteComment = useCallback((comment: WorkComment) => {
        if (!currentUserId) {
            return false;
        }

        return currentUserId === comment.authorId || currentUserId === threadOwnerUserId;
    }, [currentUserId, threadOwnerUserId]);

    const handleDeleteComment = useCallback(async (commentId: string | number) => {
        if (!threadId) {
            return;
        }

        const targetId = String(commentId);
        const targetComment = findCommentInTree(comments, targetId);
        if (!targetComment) {
            return;
        }

        const replyCount = Math.max(0, countCommentBranch(targetComment) - 1);
        const result = await confirm({
            title: '删除评论？',
            message: replyCount > 0
                ? `删除后，这条评论以及其下 ${replyCount} 条回复都会一起移除。`
                : '删除后将无法恢复。',
            confirmText: '删除',
            cancelText: '取消',
            tone: 'danger'
        });

        if (result.action !== 'confirm') {
            return;
        }

        setDeletingCommentIds((prev) => [...prev, targetId]);
        setOpenCommentMenuId(null);

        const response = await postService.deleteComment(threadId, targetId);
        setDeletingCommentIds((prev) => prev.filter((id) => id !== targetId));

        if (!response.success) {
            showToast(response.error || '评论删除失败', 'error');
            return;
        }

        const deletedCountFromServer = response.data?.deletedCount;
        const removal = removeCommentFromTree(comments, targetId);
        setComments(removal.nextComments);
        setCommentCount((prev) => Math.max(0, prev - (deletedCountFromServer ?? (removal.removedCount || 1))));

        if (replyTarget && !findCommentInTree(removal.nextComments, replyTarget.id)) {
            setReplyTarget(null);
        }

        if (threadId.startsWith('post_')) {
            window.dispatchEvent(new Event('interactions_updated'));
        }

        showToast('评论已删除', 'success');
    }, [comments, confirm, replyTarget, showToast, threadId]);

    const renderComment = (comment: WorkComment, depth = 0) => {
        const isReply = depth > 0;
        const avatarFallback = (comment.username || 'U').slice(0, 1).toUpperCase();
        const isLiking = likingCommentIds.includes(String(comment.id));
        const isDeleting = deletingCommentIds.includes(String(comment.id));
        const canDelete = canDeleteComment(comment);
        const isMenuOpen = openCommentMenuId === String(comment.id);

        return (
            <div
                key={String(comment.id)}
                className={`${isReply ? 'mt-3 pl-3 border-l-2 border-gray-100' : 'mb-4'}`}
            >
                <div className="group flex gap-3">
                    {comment.avatar ? (
                        <img
                            src={comment.avatar}
                            alt={comment.username}
                            className={`${isReply ? 'w-6 h-6' : 'w-8 h-8'} rounded-full object-cover flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity`}
                            onClick={() => comment.authorId && navigate(`/profile/${comment.authorId}`)}
                        />
                    ) : (
                        <div
                            className={`${isReply ? 'w-6 h-6 text-[10px]' : 'w-8 h-8 text-xs'} rounded-full bg-gray-200 text-gray-600 font-semibold flex items-center justify-center flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity`}
                            onClick={() => comment.authorId && navigate(`/profile/${comment.authorId}`)}
                        >
                            {avatarFallback}
                        </div>
                    )}
                    <div className="flex-1 min-w-0">
                        <div className="mb-1 flex items-start justify-between gap-3">
                            <span
                                className={`${isReply ? 'text-xs' : 'text-sm'} font-medium text-gray-900 cursor-pointer hover:opacity-80 transition-opacity`}
                                onClick={() => comment.authorId && navigate(`/profile/${comment.authorId}`)}
                            >
                                {comment.username}
                            </span>
                            {canDelete ? (
                                <div className="relative" data-comment-actions-root="true">
                                    <button
                                        type="button"
                                        onClick={() => setOpenCommentMenuId((prev) => prev === String(comment.id) ? null : String(comment.id))}
                                        className={`rounded-full p-1 text-gray-300 transition hover:bg-gray-100 hover:text-gray-600 ${isMenuOpen ? 'bg-gray-100 text-gray-600' : 'opacity-0 group-hover:opacity-100 focus:opacity-100'}`}
                                        aria-label="评论操作"
                                    >
                                        <MoreHorizontal className="h-4 w-4" />
                                    </button>

                                    {isMenuOpen ? (
                                        <div className="absolute right-0 top-full z-10 mt-1 min-w-[120px] rounded-xl border border-gray-200 bg-white p-1 shadow-lg">
                                            <button
                                                type="button"
                                                onClick={() => void handleDeleteComment(comment.id)}
                                                disabled={isDeleting}
                                                className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                                {isReply ? '删除回复' : '删除评论'}
                                            </button>
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}
                        </div>
                        <p className={`${isReply ? 'text-xs' : 'text-sm'} text-gray-700 leading-relaxed mb-1 whitespace-pre-wrap`}>
                            {comment.content}
                        </p>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                            <span>{comment.time}</span>
                            {comment.location ? <span>{comment.location}</span> : null}
                            <button
                                type="button"
                                onClick={() => void handleToggleCommentLike(comment.id)}
                                disabled={!threadId || isLiking}
                                className={`flex items-center gap-1 hover:text-gray-700 disabled:text-gray-300 ${comment.liked ? 'text-red-500 hover:text-red-500' : ''}`}
                            >
                                <Heart className={`w-3.5 h-3.5 ${comment.liked ? 'fill-current' : ''}`} />
                                {comment.likes}
                            </button>
                            {depth === 0 ? (
                                <button
                                    type="button"
                                    onClick={() => setReplyTarget({ id: String(comment.id), username: comment.username })}
                                    disabled={!canOperateComments}
                                    className="hover:text-gray-700 disabled:text-gray-300"
                                >
                                    回复
                                </button>
                            ) : null}
                        </div>

                        {comment.replies?.length ? (
                            <div className="mt-1">
                                {comment.replies.map((reply) => renderComment(reply, depth + 1))}
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="flex flex-1 min-h-0 flex-col">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <span className="text-gray-600 text-sm">共 {commentCount} 条评论</span>
                {replyTarget ? (
                    <button
                        type="button"
                        onClick={() => setReplyTarget(null)}
                        className="text-xs text-blue-500 hover:underline"
                    >
                        取消回复 @{replyTarget.username}
                    </button>
                ) : null}
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-3">
                {isLoadingComments && comments.length === 0 ? (
                    <div className="text-sm text-gray-500 py-8 text-center">正在加载评论...</div>
                ) : comments.length ? (
                    comments.map((comment) => renderComment(comment))
                ) : (
                    <div className="text-sm text-gray-400 py-8 text-center">暂无评论</div>
                )}
            </div>

            <div className="border-t border-gray-100 p-4">
                {interactionBar ? (
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-6">
                            <button
                                type="button"
                                onClick={() => void interactionBar.onToggleLike?.()}
                                disabled={!interactionBar.canToggleLike}
                                className={`flex items-center gap-1.5 transition-colors disabled:cursor-default disabled:text-gray-400 ${interactionBar.isLiked ? 'text-red-500' : 'text-gray-600 hover:text-gray-900'}`}
                            >
                                <Heart className={`w-6 h-6 ${interactionBar.isLiked ? 'fill-current' : ''}`} />
                                <span className="text-sm font-medium">{interactionBar.likeCount}</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => void interactionBar.onToggleBookmark?.()}
                                disabled={!interactionBar.canToggleBookmark}
                                className="flex items-center gap-1.5 text-gray-600 hover:text-gray-900 transition-colors disabled:cursor-default disabled:text-gray-400"
                            >
                                <Bookmark className={`w-6 h-6 ${interactionBar.isFavorited ? 'fill-current text-yellow-500' : ''}`} />
                                <span className="text-sm font-medium">{interactionBar.favoriteCount}</span>
                            </button>
                            <div className="flex items-center gap-1.5 text-gray-600">
                                <MessageCircle className="w-6 h-6" />
                                <span className="text-sm font-medium">{commentCount}</span>
                            </div>
                        </div>
                        {interactionBar.onShare ? (
                            <button
                                type="button"
                                onClick={interactionBar.onShare}
                                className={`transition-colors ${interactionBar.shareCopied ? 'text-blue-600' : 'text-gray-600 hover:text-gray-900'}`}
                            >
                                {interactionBar.shareCopied ? <Link2 className="w-5 h-5" /> : <Share2 className="w-5 h-5" />}
                            </button>
                        ) : null}
                    </div>
                ) : null}

                <div className="flex items-center gap-3">
                    <input
                        type="text"
                        placeholder={resolvedPlaceholder}
                        value={commentText}
                        onChange={(event) => setCommentText(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                void handleSubmitComment();
                            }
                        }}
                        disabled={!canOperateComments}
                        className="flex-1 h-10 px-4 bg-gray-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:text-gray-400"
                    />
                    <button
                        type="button"
                        onClick={() => void handleSubmitComment()}
                        className={`p-2.5 rounded-full transition-colors ${commentText.trim() && canOperateComments ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-400'}`}
                        disabled={!canOperateComments || !commentText.trim() || isSubmittingComment}
                    >
                        <Send className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CommentThreadSection;
